/**
 * Lazy, performance-safe analytics bootstrap.
 * - Idle-time injection (requestIdleCallback with timeout fallback)
 * - Idempotent: safe to call on every consent change / page load
 * - Skips entirely if user hasn't granted analytics consent
 */
import { analyticsConfig } from './config';
import { hasAnalyticsConsent, subscribeConsent } from './consent';
import { initGA4, disableGA4 } from './ga4';
import { initHotjar } from './hotjar';

let bootstrapped = false;
let scheduled = false;
let subscribed = false;

const scheduleIdle = (cb: () => void) => {
  if (typeof window === 'undefined') return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ric = (window as any).requestIdleCallback as
    | ((fn: () => void, opts?: { timeout: number }) => number)
    | undefined;
  if (typeof ric === 'function') {
    ric(cb, { timeout: analyticsConfig.loadDelayMs });
  } else {
    window.setTimeout(cb, analyticsConfig.loadDelayMs);
  }
};

const loadAll = () => {
  if (bootstrapped) return;
  if (!hasAnalyticsConsent()) return;
  bootstrapped = true;
  initGA4();
  initHotjar();
};

/**
 * Initialize the analytics pipeline.
 * Safe to call multiple times — subscriber is registered only once,
 * scripts are loaded only once, and re-calls are cheap no-ops.
 */
export const initAnalytics = (): void => {
  if (typeof window === 'undefined') return;

  // Register consent listener exactly once for the lifetime of the page.
  if (!subscribed) {
    subscribed = true;
    subscribeConsent((next) => {
      if (next.analytics && !bootstrapped && !scheduled) {
        scheduled = true;
        scheduleIdle(loadAll);
      } else if (!next.analytics && bootstrapped) {
        // We can't fully unload third-party scripts mid-session, but we can stop GA collection.
        disableGA4();
        if (analyticsConfig.debug) console.info('[analytics] consent revoked — GA4 disabled');
      }
    });
  }

  if (bootstrapped || scheduled) return;
  if (!hasAnalyticsConsent()) return;

  scheduled = true;
  scheduleIdle(loadAll);
};
